class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');

    this.searchModalActionOpen = document.querySelector('#search-modal-action-open');
    this.searchModalActionClose = document.querySelector('#search-modal-action-close');
    this.searchModal = document.querySelector('#search-modal');
    this.isOpen = false;
    this.isModalOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.debounceTimeout = null;

    this.setupEventListeners();
  }

  static getBrandScopeData() {
    const brandScope = sessionStorage.getItem('brandScope');
    if (!brandScope) return null;

    const brandHandle = brandScope.toLowerCase().replace(/\s+/g, '-').trim();
    const brandFilter = brandScope
      .replace(/-/g, ' ')
      .split(' ')
      .map((word, index) => {
        const lower = word.toLowerCase();
        if ((lower === 'de' || lower === 'du') && index !== 0) return lower;
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join(' ');

    return {
      brandScope,
      brandHandle,
      brandFilter,
      isDefault: brandHandle === 'home-society',
    };
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    this.searchModalActionOpen.addEventListener('click', () => this.openMobileModal());
    this.searchModalActionClose.addEventListener('click', () => this.closeMobileModal());

    if (window.innerWidth < 1024) {
      document.addEventListener('click', this.onDocumentClick.bind(this));
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    // Debounce after client finish typing
    clearTimeout(this.debounceTimeout);
    this.debounceTimeout = setTimeout(() => {
      if (this.searchTerm.length > 2) {
        this.getSearchResults(this.searchTerm);
      } else {
        let backdropElement = this.predictiveSearchResults.querySelector('.back-drop');
        const resultGroup = this.predictiveSearchResults.querySelector(
          '[data-predictive-search-live-region-count-value]'
        );

        if (!backdropElement) {
          backdropElement = document.createElement('div');
          backdropElement.className = 'back-drop';
          this.predictiveSearchResults.appendChild(backdropElement);
        }

        resultGroup.innerText = this.predictiveSearchResults.dataset.termUnder;
        resultGroup.classList.remove('hidden');

        if (window.innerWidth > 1023 && !document.body.classList.contains('overflow-hidden')) {
          document.body.classList.add('overflow-hidden');
        }

        if (!this.isOpen) {
          this.predictiveSearchResults.querySelector('.back-drop').style.opacity = '0';
        }

        this.open();
        this.predictiveSearchResults.querySelector('.back-drop').style.opacity = '1';
      }
    }, 75);
  }

  onFormSubmit(event) {
  const selectedLink = this.querySelector('[aria-selected="true"] a');
  event.preventDefault();

  if (selectedLink) {
    selectedLink.click();
    return;
  }

  const form = event.target.closest('form');
  const searchTerm = this.getQuery();
  const redirectUrl = new URL(form.action, window.location.origin);
  redirectUrl.searchParams.set('q', searchTerm);
  redirectUrl.searchParams.set('options[prefix]', 'last');
  
  window.location.href = redirectUrl.toString();
}

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    this.searchModal.querySelector('.search-modal').setAttribute('data-selected', true);

    if (currentSearchTerm.length < 3) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    if (!this.searchTerm) {
      this.searchModal.querySelector('.search-modal').removeAttribute('data-selected');
    }

    setTimeout(() => {
      if (window.innerWidth > 1023 && !this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }

    if (event.code === 'Enter' && !this.searchTerm) {
      event.preventDefault();
    }
  }

  onDocumentClick(event) {
    // Check if the clicked element is not within the header .header-wrapper
    const headerWrapper = document.querySelector('.header-wrapper');
    if (!headerWrapper.contains(event.target)) {
      this.closeMobileModal();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
  const queryKey = searchTerm.replace(' ', '-').toLowerCase();
  this.setLiveRegionLoadingState();

  if (this.cachedResults[queryKey]) {
    this.renderSearchResults(this.cachedResults[queryKey]);
    return;
  }

  //  START : Modification GMS 04/11/25  //
  const url =
    `${routes.predictive_search_url}` +
    `?q=${encodeURIComponent(searchTerm)}` +
    `&options[prefix]=last` +                                   
    `&section_id=predictive-search` +                           
    `&resources[type]=product,collection,page,article,query` +  
    `&resources[limit]=20` +                                    
    `&resources[options][fields]=title,product_type,variants.sku,body,tag` +
    `&resources[options][unavailable_products]=last`;

  fetch(url, { signal: this.abortController.signal })
  
    .then((response) => {
      if (!response.ok) {
        var error = new Error(response.status);
        this.close();
        throw error;
      }
      return response.text();
    })
    .then((text) => {
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const resultsMarkup = doc.querySelector('#shopify-section-predictive-search');

      
      resultsMarkup.querySelectorAll('.predictive-search__list-item a').forEach((anchor) => {
        const containsHTMLTags = /<\/?[a-z][\s\S]*>/i.test(anchor.innerHTML);
        if (containsHTMLTags) {
          if (!anchor.innerHTML.includes(`<mark>${queryKey}</mark>`)) {
            const regex = new RegExp(`>([^<]*?)(${queryKey})([^<]*?)<`, 'gi');
            anchor.innerHTML = anchor.innerHTML.replace(
              regex,
              (match, p1, p2, p3) => `>${p1}<mark>${p2}</mark>${p3}<`
            );
          }
        } else {
          const regex = new RegExp(`(${queryKey})`, 'gi');
          anchor.innerHTML = anchor.innerHTML.replace(regex, '<mark>$1</mark>');
        }
      });

      
      this.allPredictiveSearchInstances.forEach((inst) => {
        inst.cachedResults[queryKey] = resultsMarkup.innerHTML;
      });
      this.renderSearchResults(resultsMarkup.innerHTML);
    })
    .catch((error) => {
      if (error?.code === 20) return; 
      this.close();
      throw error;
    });
}
// END //

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    // Add brand filter to query links if applicable
    const brandData = PredictiveSearch.getBrandScopeData();
    if (brandData && !brandData.isDefault) {
      // Normalise: retire accents, minuscule, espaces -> tirets
      const normalize = (s) =>
        (s || '')
          .toString()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .replace(/[^a-z0-9\- ]+/g, '')
          .replace(/\s+/g, '-');

      const brandHandle = normalize(brandData.brandHandle);
      const allProductItems = this.predictiveSearchResults.querySelectorAll('[data-product-brand]');
      let kept = 0;

      allProductItems.forEach((item) => {
        const itemBrand = normalize(item.getAttribute('data-product-brand'));
        const keep = (brandHandle === 'home-society') || itemBrand.includes(brandHandle);
        if (keep) {
          item.style.display = '';
          kept++;
        } else {
          item.style.display = 'none'; // on masque (pas remove)
        }
      });

      // Fallback : si 0 produit gardé, montre tout (évite colonne vide)
      if (kept === 0) {
        allProductItems.forEach((item) => (item.style.display = ''));
      }
    }

    const brandScope = brandData ? brandData.brandScope : null;
    const rawBrandHandle = brandData ? brandData.brandHandle : null;

    if (brandScope) {
      const normalize = (s) =>
        (s || '')
          .toString()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .replace(/[^a-z0-9\- ]+/g, '')
          .replace(/\s+/g, '-');

      const brandHandleNorm = normalize(rawBrandHandle);

      
      this.predictiveSearchResults
        .querySelectorAll('[data-product-brand]')
        .forEach((item) => (item.dataset.brandScope = brandScope));

      
      const allCollections = this.predictiveSearchResults.querySelectorAll('[href*="/collections/"]');
      allCollections.forEach((item) => {
        const txt = normalize(item.textContent);
        if (brandHandleNorm !== 'home-society' && !txt.includes(brandHandleNorm)) {
          item.closest('li')?.remove();
        }
      });

      
      const allPages = this.predictiveSearchResults.querySelectorAll('[href^="/pages/"]');
      allPages.forEach((item) => {
        const txt = normalize(item.textContent);
        if (brandHandleNorm !== 'home-society' && !txt.includes(brandHandleNorm)) {
          item.closest('li')?.remove();
        }
      });
    }

    // ---- APERÇU DANS D’AUTRES MARQUES (même style que la colonne produits) ----
try {
  const productsWrapper = this.predictiveSearchResults.querySelector('.predictive-search__product-wrapper');
  const productGrid = this.predictiveSearchResults.querySelector('#predictive-search-results-products-list');

  if (productsWrapper && productGrid) {
    // produits MASQUÉS par le scope (display:none)
    const hiddenItems = Array.from(productGrid.querySelectorAll('[data-product-brand]'))
      .filter((li) => li.style.display === 'none' || getComputedStyle(li).display === 'none');

    if (hiddenItems.length > 0) {
      // wrapper avec les mêmes classes que la colonne principale
      const otherWrap = document.createElement('div');
      otherWrap.className = 'predictive-search__product-wrapper predictive-search__product-wrapper--other';

      otherWrap.innerHTML = `
        <h2 class="predictive-search__heading typo-header-sm typo-light">
          ${document.documentElement.lang === 'fr' ? "Disponible dans d’autres marques" : "Available from other brands"}
        </h2>
        <ul id="predictive-search-results-products-list-other"
            class="predictive-search__results-list grid product-grid predictive-search__product-grid"
            role="group"
            aria-label="Aussi dans d’autres marques">
        </ul>
      `;

      const otherUL = otherWrap.querySelector('#predictive-search-results-products-list-other');

      // clone jusqu’à 3 produits cachés – et retire tout style inline qui masque
      hiddenItems.slice(0, 3).forEach((li) => {
        const clone = li.cloneNode(true);
        clone.style.removeProperty('display');
        clone.removeAttribute('style');
        otherUL.appendChild(clone);
      });

      // Insère ce bloc juste après la colonne produits principale (même style)
      productsWrapper.insertAdjacentElement('afterend', otherWrap);
    }
  }
} catch (e) {
  console.warn('Aperçu autres marques : échec non bloquant', e);
}
// ---- FIN APERÇU DANS D’AUTRES MARQUES ----

    const queriesList = this.predictiveSearchResults.querySelector('#predictive-search-results-queries-list');

    // Hide the product wrapper if no products remain
    const productsWrapper = this.predictiveSearchResults.querySelector('.predictive-search__product-wrapper');
    const productsList = this.predictiveSearchResults.querySelector('#predictive-search-results-products-list');

    if (productsList?.children?.length === 0) {
      productsWrapper.style.display = 'none';
    }

    // Hide the collections wrapper if no collections remain
    const collectionsWrapper = this.predictiveSearchResults.querySelector('.predictive-search__collections-wrapper');
    const collectionsList = this.predictiveSearchResults.querySelector('.predictive-search__collections-list');
    if (collectionsList?.children?.length === 0) {
      collectionsWrapper.style.display = 'none';
    }

    // Hide the pages wrapper if no pages remain
    const pagesWrapper = this.predictiveSearchResults.querySelector('.predictive-search__pages-wrapper');
    const pagesList = this.predictiveSearchResults.querySelector('#predictive-search-results-pages-list-desktop');

    if (pagesList?.children?.length === 0) {
      pagesWrapper.style.display = 'none';
    }

    // add click event for brand scope
    if (pagesList === null && collectionsList?.innerHTML === '' && queriesList?.innerHTML === '') {
      this.predictiveSearchResults.querySelector('.predictive-search__result-group-suggestions').style.display = 'none';
    }

    if (pagesList === null && collectionsList === null && productsList === null && queriesList === null) {
      const resultGroup = this.predictiveSearchResults.querySelector(
        '[data-predictive-search-live-region-count-value]'
      );
      resultGroup.classList.remove('hidden');
    }

    if (window.innerWidth > 1023) {
      if (!this.isOpen) {
        setTimeout(() => {
          this.predictiveSearchResults.querySelector('.back-drop').style.opacity = '1';
        }, 50);
      } else {
        this.predictiveSearchResults.querySelector('.back-drop').style.opacity = '1';
      }

      if (!document.body.classList.contains('overflow-hidden')) {
        document.body.classList.add('overflow-hidden');
      }
    }

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  openMobileModal() {
    this.searchModal.style.opacity = '0';
    this.searchModal.style.display = 'block';

    setTimeout(() => {
      this.searchModal.style.opacity = '1';
    }, 50);

    this.isModalOpen = true;
    this.searchModal.setAttribute('data-selected', 'true');

    const input = this.searchModal.querySelector('input[type="search"]');
    this.iosFocus(input, 50);
  }

  iosFocus(el, timeout) {
    if (!timeout) {
      timeout = 100;
    }
    if (el) {
      const fakeInput = document.createElement('input');
      fakeInput.style.position = 'absolute';
      fakeInput.style.top = el.offsetTop + 7 + 'px';
      fakeInput.style.left = el.offsetLeft + 'px';
      fakeInput.style.height = 0;
      fakeInput.style.opacity = 0;

      document.body.appendChild(fakeInput);
      fakeInput.focus();

      setTimeout(function () {
        el.focus();
        el.click();
        // Remove the temp element
        document.body.removeChild(fakeInput);
      }, timeout);
    }
  }

  closeMobileModal() {
    this.isModalOpen = false;
    this.searchModal.style.opacity = '0';

    setTimeout(() => {
      this.searchModal.style.display = 'none';
    }, 300);

    if (document.body.classList.contains('overflow-hidden')) {
      document.body.classList.remove('overflow-hidden');
    }
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;

    document.body.classList.add('overflow-hidden');

    const backdropElement = document.querySelector('.back-drop');
    if (backdropElement) {
      backdropElement.addEventListener('click', () => this.close());
    }

    const predictiveSearchBottom = this.allPredictiveSearchInstances[0].getBoundingClientRect().bottom + 1;
    this.predictiveSearchResults.style.top = `${predictiveSearchBottom}px`;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.predictiveSearchResults.removeAttribute('style');

    if (document.body.classList.contains('overflow-hidden')) {
      document.body.classList.remove('overflow-hidden');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
